import React, { useState, useEffect } from 'react';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import { Table, Button, Container, Row, Col, Card, Badge, Modal, Pagination } from 'react-bootstrap';
import axios from 'axios';
import BaseUrl from '../../ApiServices/BaseUrl';
import './EnquiryDetail.css';

function EnquiryDetail() {
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchEnquiries();
    }, []);

    const fetchEnquiries = async () => {
        try {
            const response = await axios.get(`${BaseUrl}/api/enquiries/admin`);
            setEnquiries(response.data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return 'N/A';
        }
    };

    const handleViewEnquiry = (enquiry) => {
        setSelectedEnquiry(enquiry);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedEnquiry(null);
    };

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentEnquiries = enquiries.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(enquiries.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    if (loading) return <div className="text-center mt-5">Loading enquiries...</div>;
    if (error) return <div className="text-center mt-5 text-danger">Error: {error}</div>;

    return (
        <>
            <AdminNavbar />
            <Container className="enquiry-detail-container" style={{ marginTop: '100px', marginBottom: '50px' }}>
                <Row className="mb-4">
                    <Col>
                        <h2 className="fw-bold">Agency Enquiries</h2>
                        <p className="text-muted">Monitor user enquiries sent to recruitment agencies</p>
                    </Col>
                </Row>

                <Card className="shadow-sm border-0">
                    <Card.Body>
                        <div className="table-responsive">
                            <Table striped hover className="align-middle">
                                <thead className="bg-light">
                                    <tr>
                                        <th>S.No</th>
                                        <th>User Name</th>
                                        <th>User Type</th>
                                        <th>Agency Name</th>
                                        <th>Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentEnquiries.map((enquiry, index) => (
                                        <tr key={enquiry.id}>
                                            <td>{indexOfFirstItem + index + 1}</td>
                                            <td>
                                                <div className="fw-bold">{enquiry.name}</div>
                                                <small className="text-muted">{enquiry.email}</small>
                                            </td>
                                            <td>
                                                <Badge bg={enquiry.user_type === 'customer' ? 'primary' : 'info'} className="text-capitalize">
                                                    {enquiry.user_type}
                                                </Badge>
                                            </td>
                                            <td>{enquiry.agency_name}</td>
                                            <td>{formatDate(enquiry.created_at)}</td>
                                            <td>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => handleViewEnquiry(enquiry)}
                                                >
                                                    View Message
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {enquiries.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4">No enquiries found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {totalPages > 1 && (
                            <div className="d-flex justify-content-center mt-4">
                                <Pagination>
                                    <Pagination.Prev
                                        disabled={currentPage === 1}
                                        onClick={() => handlePageChange(currentPage - 1)}
                                    />
                                    {[...Array(totalPages).keys()].map(number => (
                                        <Pagination.Item
                                            key={number + 1}
                                            active={number + 1 === currentPage}
                                            onClick={() => handlePageChange(number + 1)}
                                        >
                                            {number + 1}
                                        </Pagination.Item>
                                    ))}
                                    <Pagination.Next
                                        disabled={currentPage === totalPages}
                                        onClick={() => handlePageChange(currentPage + 1)}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {/* Modal for viewing message */}
                <Modal show={showModal} onHide={handleCloseModal} size="md">
                    <Modal.Header closeButton>
                        <Modal.Title>Enquiry Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedEnquiry && (
                            <div>
                                <Row className="mb-3">
                                    <Col sm={4} className="fw-bold">From:</Col>
                                    <Col sm={8}>{selectedEnquiry.name} ({selectedEnquiry.user_type})</Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col sm={4} className="fw-bold">Email:</Col>
                                    <Col sm={8}>{selectedEnquiry.email}</Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col sm={4} className="fw-bold">Phone:</Col>
                                    <Col sm={8}>{selectedEnquiry.phone_number}</Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col sm={4} className="fw-bold">To Agency:</Col>
                                    <Col sm={8}>{selectedEnquiry.agency_name}</Col>
                                </Row>
                                <hr />
                                <div>
                                    <div className="fw-bold mb-2">Message:</div>
                                    <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedEnquiry.message}
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </>
    );
}

export default EnquiryDetail;
